'use client';
import React, { useEffect, useState } from 'react';

type Category = 'Health Advisory' | 'Health News' | 'Surge Alert' | 'Tip';

interface FeedItem {
  id: string;
  category: Category;
  headline: string;
  description: string;
  timestamp: string;
  url: string;
  fetchedAt: number;
}

const categoryConfig: Record<Category, { bg: string; text: string; border: string }> = {
  'Health Advisory': { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' },
  'Surge Alert':     { bg: '#fefce8', text: '#92400e', border: '#fde68a' },
  'Health News':     { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  Tip:               { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
};

function timeAgo(fetchedAt: number): string {
  const diff = Math.floor((Date.now() - fetchedAt) / 60000);
  if (diff < 1) return 'Refreshed just now';
  if (diff === 1) return 'Refreshed 1 min ago';
  if (diff < 60) return `Refreshed ${diff} min ago`;
  return `Refreshed ${Math.floor(diff / 60)}h ago`;
}

function SkeletonCard() {
  return (
    <div className="px-6 py-4 animate-pulse">
      <div className="h-4 w-24 bg-[#e4eaf5] rounded-full mb-3" />
      <div className="h-4 w-full bg-[#e4eaf5] rounded mb-2" />
      <div className="h-3 w-3/4 bg-[#e4eaf5] rounded mb-3" />
      <div className="h-3 w-16 bg-[#e4eaf5] rounded" />
    </div>
  );
}

export default function NSHealthUpdatesFeed() {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/api/ns-health-feed')
      .then((r) => r.json())
      .then((data) => {
        setItems(data.items ?? []);
        setFetchedAt(data.fetchedAt ?? Date.now());
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  return (
    <div>
      {/* Section header */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-[#1a2744] leading-tight">NS Health News</h2>
        {/* Live pulse dot */}
        <span className="relative flex items-center justify-center w-3 h-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22c55e] opacity-60" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#16a34a]" />
        </span>
      </div>

      {/* Feed cards */}
      <div className="bg-white rounded-2xl border border-[#e4eaf5] overflow-hidden divide-y divide-[#e4eaf5]">
        {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}

        {error && (
          <div className="px-6 py-8 text-center text-sm text-[#6b7a99]">
            Unable to load health updates right now. Please check back shortly.
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="px-6 py-8 text-center text-sm text-[#6b7a99]">
            No updates found at this time.
          </div>
        )}

        {!loading && !error && items.map((item) => {
          const cfg = categoryConfig[item.category] ?? categoryConfig['Health News'];
          return (
            <a
              key={item.id}
              href={item.url || '#'}
              target={item.url ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="block px-6 py-4 hover:bg-[#f8faff] transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  {/* Badge */}
                  <span
                    className="inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full mb-2 border"
                    style={{ backgroundColor: cfg.bg, color: cfg.text, borderColor: cfg.border }}
                  >
                    {item.category}
                  </span>
                  {/* Headline */}
                  <p className="text-sm font-bold text-[#1a2744] leading-snug mb-1">
                    {item.headline}
                  </p>
                  {/* Description */}
                  {item.description && (
                    <p className="text-sm text-[#6b7a99] leading-relaxed mb-2">
                      {item.description}
                    </p>
                  )}
                  {/* Timestamp */}
                  <p className="text-xs text-[#9aa5be] font-mono">{item.timestamp}</p>
                </div>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
